// AI asistent – vanilla JS chat přes Vercel AI Gateway.
//
// Komunikuje s /api/ai/chat (SSE UI message stream z AI SDK v5) a /api/ai/models.
// Tooly (calculate_price, list_materials, nabídky…) běží na backendu přes
// service vrstvu – frontend jen zobrazuje streamované odpovědi a tool chipy.
// CSRF hlavičku a credentials přidává globální csrfFetch wrapper z app.js.

import { marked } from 'marked';
import { hydrateIcons } from './icons.js';

const API_BASE_URL = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
  ? 'http://localhost:1234/api'
  : '/api';

const STORAGE_KEY = 'tria-ai-chats';
const LEGACY_KEY = 'tria-ai-chat';
const MAX_CHATS = 100;

const state = {
  chats: [],          // [{ id, title, createdAt, updatedAt, modelId, messages }]
  activeChatId: null, // ID aktuálně otevřeného chatu
  modelId: null,
  availableModels: [],
  defaultModel: 'deepseek/deepseek-v4-flash',
  abortController: null,
  streaming: false,
  initialised: false,
  historySearch: '',
};

let el = {};

function $(id) { return document.getElementById(id); }

function showError(msg) {
  if (!el.error) return;
  el.error.textContent = msg || '';
  el.error.classList.toggle('hidden', !msg);
}

function scrollToBottom() {
  if (el.messages) el.messages.scrollTop = el.messages.scrollHeight;
}

function renderMarkdown(text) {
  try {
    return marked.parse(text || '', { breaks: true, gfm: true });
  } catch {
    return (text || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }
}

function escapeHtml(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Vytvoří prázdnou bublinu asistenta a vrátí {root, tools, content, setText, addTool, updateTool, appendReasoning}. */
function createAssistantBubble() {
  const root = document.createElement('div');
  root.className = 'flex flex-col gap-2 max-w-[85%]';
  root.innerHTML = `
    <div class="ai-tools flex flex-wrap gap-1.5"></div>
    <details class="ai-reasoning hidden text-xs text-ink-muted border border-gray-200 rounded-md bg-gray-50 px-2.5 py-1.5">
      <summary class="cursor-pointer select-none font-medium text-ink-muted">Přemýšlení</summary>
      <div class="ai-reasoning-content mt-1.5 whitespace-pre-wrap leading-relaxed"></div>
    </details>
    <div class="bg-gray-50 border border-gray-200 rounded-2xl rounded-tl-sm p-3 text-sm text-ink leading-relaxed ai-content prose prose-sm max-w-none">
      <div class="ai-thinking inline-flex items-center gap-1.5 text-ink-muted">
        <span class="inline-flex gap-1 items-center">
          <span class="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style="animation-delay:0ms"></span>
          <span class="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style="animation-delay:150ms"></span>
          <span class="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style="animation-delay:300ms"></span>
        </span>
        <span>Asistent přemýšlí…</span>
      </div>
    </div>
    <div class="ai-offers flex flex-col gap-2"></div>
  `;
  el.messages.appendChild(root);
  const toolsEl = root.querySelector('.ai-tools');
  const contentEl = root.querySelector('.ai-content');
  const offersEl = root.querySelector('.ai-offers');
  const thinkingEl = root.querySelector('.ai-thinking');
  const reasoningEl = root.querySelector('.ai-reasoning');
  const reasoningContentEl = root.querySelector('.ai-reasoning-content');
  const toolChips = new Map();
  let hasContent = false;

  function hideThinking() {
    if (thinkingEl && thinkingEl.parentNode) thinkingEl.remove();
  }

  return {
    root,
    setText(text) {
      if (text && text.trim()) {
        hasContent = true;
        hideThinking();
        contentEl.innerHTML = renderMarkdown(text);
      } else if (!hasContent) {
        // ještě nic nepřišlo – nech thinking indikátor
      }
    },
    addTool(toolCallId, toolName) {
      hideThinking();
      // Stream může pro stejné toolCallId poslat tool-input-start opakovaně
      // (např. při paralelních voláních v jednom kroku). Vytvoř chip jen jednou.
      const existing = toolChips.get(toolCallId);
      if (existing) return existing;
      const chip = document.createElement('span');
      chip.className = 'inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-purple-50 text-purple-700 border border-purple-200';
      chip.dataset.toolName = toolName || '';
      chip.innerHTML = `
        <svg class="w-3.5 h-3.5 animate-spin" data-icon="spinner"></svg>
        <span>Používám nástroj: <strong>${escapeHtml(toolName)}</strong></span>
      `;
      hydrateIcons(chip);
      toolsEl.appendChild(chip);
      toolChips.set(toolCallId, chip);
      return chip;
    },
    finishTool(toolCallId, output) {
      const chip = toolChips.get(toolCallId);
      if (!chip) return;
      const toolName = chip.dataset.toolName || '';
      const isError = output && typeof output === 'object' && output.error;
      chip.className = `inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border ${isError ? 'bg-red-50 text-red-700 border-red-200' : 'bg-green-50 text-green-700 border-green-200'}`;
      chip.innerHTML = `
        <svg class="w-3.5 h-3.5" data-icon="${isError ? 'exclamation-circle' : 'check-circle'}"></svg>
        <span>${isError ? 'Chyba: ' + escapeHtml(String(output.error).slice(0, 80)) : escapeHtml(toolLabel(toolName, output))}</span>
      `;
      hydrateIcons(chip);
    },
    appendReasoning(delta) {
      if (!delta) return;
      hideThinking();
      reasoningEl.classList.remove('hidden');
      reasoningContentEl.textContent += delta;
    },
    addOfferCard(offer) {
      if (!offer || !offer.id) return;
      hideThinking();
      const card = document.createElement('div');
      card.className = 'border border-gray-200 bg-white rounded-xl px-3 py-2.5 shadow-sm flex items-start gap-3';
      const status = String(offer.status || 'draft');
      const stavLabel = STATUS_LABELS[status] || status;
      const stavClass = STATUS_BADGE[status] || 'bg-gray-100 text-gray-700 border-gray-200';
      const polozkyPocet = offer.polozkyPocet != null ? offer.polozkyPocet : (Array.isArray(offer.polozky) ? offer.polozky.length : null);
      const cena = Number(offer.cenaCelkem != null ? offer.cenaCelkem : offer.totalPrice) || 0;
      const zakaznik = offer.zakaznik || offer.customerName || '—';
      const created = offer.createdAt ? new Date(offer.createdAt).toLocaleDateString('cs-CZ') : '';
      card.innerHTML = `
        <div class="shrink-0 w-9 h-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
          <svg class="w-5 h-5" data-icon="document"></svg>
        </div>
        <div class="min-w-0 flex-1">
          <div class="flex items-center gap-2 flex-wrap">
            <span class="font-semibold text-ink text-sm">${escapeHtml(offer.id)}</span>
            <span class="text-[11px] px-1.5 py-0.5 rounded-full border ${stavClass}">${escapeHtml(stavLabel)}</span>
          </div>
          <div class="mt-0.5 text-xs text-ink-muted leading-relaxed">
            <div><span class="text-gray-500">Zákazník:</span> <span class="text-ink">${escapeHtml(zakaznik)}</span></div>
            <div class="flex gap-3 flex-wrap">
              ${polozkyPocet != null ? `<span><span class="text-gray-500">Položek:</span> <span class="text-ink">${polozkyPocet}</span></span>` : ''}
              <span><span class="text-gray-500">Celkem:</span> <span class="text-ink font-medium">${cena.toLocaleString('cs-CZ')} Kč</span></span>
              ${created ? `<span><span class="text-gray-500">Vytvořeno:</span> <span class="text-ink">${created}</span></span>` : ''}
            </div>
          </div>
        </div>
        <button type="button" class="offer-open-btn shrink-0 inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:text-blue-800 hover:bg-blue-50 px-2 py-1.5 rounded-md border border-blue-200">
          Otevřít
          <svg class="w-3.5 h-3.5" data-icon="arrow-right"></svg>
        </button>
      `;
      card.querySelector('.offer-open-btn').addEventListener('click', () => {
        if (window.offersListManager && typeof window.offersListManager.openOfferEditor === 'function') {
          window.offersListManager.openOfferEditor(offer.id);
        } else if (window.sectionManager) {
          window.sectionManager.showSection('nabidky');
        }
      });
      offersEl.appendChild(card);
      hydrateIcons(card);
    },
    addCalcButton(input) {
      if (!input || typeof input !== 'object') return;
      hideThinking();
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'self-start inline-flex items-center gap-1.5 text-xs font-medium text-blue-700 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-2.5 py-1.5 rounded-md border border-blue-200';
      // U vícepoložkových poptávek (letáky + vizitky…) musí být z tlačítka
      // poznat, ke které kalkulaci patří – popisek z nazev/rozměru + počtu ks.
      const parts = [];
      if (input.nazev) parts.push(String(input.nazev));
      else if (input.sirka && input.vyska) parts.push(`${input.sirka}×${input.vyska} mm`);
      if (input.pocetKusu) parts.push(`${Number(input.pocetKusu).toLocaleString('cs-CZ')} ks`);
      const label = parts.join(', ');
      btn.innerHTML = `
        <svg class="w-3.5 h-3.5 shrink-0" data-icon="calculator"></svg>
        Otevřít v kalkulátoru${label ? `:&nbsp;<span class="font-semibold truncate max-w-[16rem]">${escapeHtml(label)}</span>` : ''}
      `;
      btn.addEventListener('click', () => loadCalcIntoForm(input));
      offersEl.appendChild(btn);
      hydrateIcons(btn);
    },
    clearThinking() {
      hideThinking();
    },
  };
}

const STATUS_LABELS = {
  draft: 'Draft',
  sent: 'Odesláno',
  negotiation: 'Jednání',
  approved: 'Schváleno',
  rejected: 'Odmítnuto',
  converted: 'Převedeno',
};
const STATUS_BADGE = {
  draft: 'bg-gray-100 text-gray-700 border-gray-200',
  sent: 'bg-blue-50 text-blue-700 border-blue-200',
  negotiation: 'bg-amber-50 text-amber-700 border-amber-200',
  approved: 'bg-green-50 text-green-700 border-green-200',
  rejected: 'bg-red-50 text-red-700 border-red-200',
  converted: 'bg-purple-50 text-purple-700 border-purple-200',
};

/** Z tool outputu vytáhne seznam nabídek k zobrazení jako vizitka (max 3). */
function extractOffers(toolName, output) {
  if (!output || typeof output !== 'object' || output.error) return [];
  if (toolName === 'create_offer') {
    if (output.id) {
      return [{
        id: output.id,
        status: output.status,
        zakaznik: output.customerName,
        cenaCelkem: output.totalPrice,
        createdAt: output.createdAt,
      }];
    }
    return [];
  }
  if (toolName === 'get_offer') {
    const s = output.summary || (output.offer && summarizeOfferLocally(output.offer));
    return s ? [s] : [];
  }
  if (toolName === 'get_offers') {
    const list = Array.isArray(output.offers) ? output.offers : [];
    return list.slice(0, 3);
  }
  return [];
}

function summarizeOfferLocally(offer) {
  if (!offer) return null;
  const items = offer.items || offer.polozky || [];
  const total = items.reduce((s, it) => s + (Number(it.cena) || Number(it.price) || 0), 0);
  return {
    id: offer.id,
    status: offer.status,
    zakaznik: offer.customerName || offer.nazevZakaznika || offer.zakaznik || '',
    polozkyPocet: items.length,
    cenaCelkem: offer.totalPrice != null ? offer.totalPrice : total,
    createdAt: offer.createdAt,
  };
}

// Mapování názvu laminace z calculate_price (tool) na value ve formuláři <select id="laminace">.
const LAMINACE_MAP = {
  '0': '0',
  'lesk-jednostranna': 'lesk10',
  'lesk-oboustranna': 'lesk11',
  'mat-jednostranna': 'mat10',
  'mat-oboustranna': 'mat11',
  'struktura-jednostranna': 'struktura10',
  'struktura-oboustranna': 'struktura11',
  'samet-jednostranna': 'samet10',
  'samet-oboustranna': 'samet11',
};
// Totéž pro lakování → <select id="lakovani">.
const LAKOVANI_MAP = {
  '0': '0',
  'parcialni-jednostranne': 'parcial10',
  'parcialni-oboustranne': 'parcial11',
  'plocha-jednostranne': 'plocha10',
  'plocha-oboustranne': 'plocha11',
  'stroj-jednostranne': 'stroj10',
  'stroj-oboustranne': 'stroj11',
};

/**
 * Převede vstupní argumenty nástroje calculate_price na objekt formData,
 * který lze předat offerItemsManager.restoreFormState() – formulář kalkulátoru
 * se tím naplní stejnými hodnotami, jaké zadal AI agent.
 */
function calcInputToFormData(args) {
  const fd = {};
  if (args.nazev != null) fd.nazev = String(args.nazev);
  if (args.pocetKusu != null) fd.pocetKusu = String(args.pocetKusu);
  if (args.sirka != null && args.vyska != null) {
    fd.produktRozmerFormat = 'vlastni';
    fd.produktRozmerSirka = String(args.sirka);
    fd.produktRozmerVyska = String(args.vyska);
  }
  if (args.barevnost != null) fd.pocetBarev = String(args.barevnost);
  if (args.primeBarvy != null) fd.primeBarvy = String(args.primeBarvy);
  if (args.material != null) fd.materialName = String(args.material);
  if (args.laminace != null) fd.laminace = LAMINACE_MAP[String(args.laminace)] || '0';
  if (args.lakovani != null) fd.lakovani = LAKOVANI_MAP[String(args.lakovani)] || '0';
  if (args.falcovani != null) fd.falcovani = String(args.falcovani).replace(/_/g, '-');
  if (args.bigovani != null) fd.bigovani = String(args.bigovani).replace(/_/g, '-');
  if (args.vrtani != null) fd.vrtani = String(args.vrtani);
  if (args.perforace != null) fd.perforace = String(args.perforace);
  if (args.dtp != null) fd.dtp = String(args.dtp);
  if (args.vysek != null) {
    const n = Number(args.vysek) || 0;
    if (n > 0) {
      fd.vysek = 'nove';
      fd.pozicNaVysek = String(n);
    } else {
      fd.vysek = '0';
    }
  }
  if (args.vazba != null) fd.vazba = String(args.vazba);
  if (args.vazbaPocetStran != null) fd.vazbaPocetStran = String(args.vazbaPocetStran);
  if (args.vazbaJinaObalka != null) fd.vazbaJinaObalka = Boolean(args.vazbaJinaObalka);
  if (args.vnitrniMaterial != null) fd.vnitrniMaterialName = String(args.vnitrniMaterial);
  if (args.vnitrniBarevnost != null) fd.vnitrniBarevnost = String(args.vnitrniBarevnost);
  if (args.vnitrniLaminace != null) fd.vnitrniLaminace = LAMINACE_MAP[String(args.vnitrniLaminace)] || '0';
  return fd;
}

/**
 * Načte vstupní argumenty calculate_price do formuláře kalkulátoru a přepne
 * do sekce Kalkulátor. Přidá novou prázdnou položku (jako by uživatel klikl
 * „Přidat položku"), aby se hodnoty nepřepsaly nad existující rozpracovanou
 * položku nabídky.
 */
function loadCalcIntoForm(args) {
  const mgr = window.offerItemsManager;
  if (!mgr || typeof mgr.restoreFormState !== 'function') {
    if (window.showNotification) window.showNotification('Kalkulátor není k dispozici.', 'error');
    return;
  }
  if (window.sectionManager) window.sectionManager.showSection('kalkulator');
  mgr.addItem();
  const formData = calcInputToFormData(args);
  mgr.restoreFormState(formData);
  if (window.showNotification) window.showNotification('Výpočet načten do kalkulátoru', 'success');
}

function toolLabel(toolName, output) {
  if (!output) return 'OK';
  if (typeof output === 'string') return output.slice(0, 60);
  if (typeof output === 'object') {
    // Nejprve konkrétní výsledky podle toolu
    if (toolName === 'calculate_price' && output.cenaCelkem != null) {
      return `Spočítáno: ${output.cenaCelkem} Kč`;
    }
    if (toolName === 'create_offer') {
      return `Nabídka ${output.id || ''} vytvořena`;
    }
    if (toolName === 'get_offer') {
      return `Nabídka ${output.summary?.id || output.id || ''}`;
    }
    if (toolName === 'update_offer_status') {
      return `Nabídka ${output.id || ''} → ${output.status || ''}`;
    }
    if (toolName === 'reprint_order') {
      return `Objednávka ${output.id || ''} vytvořena`;
    }
    if (toolName === 'get_order') {
      return `Zakázka ${output.summary?.id || output.id || ''}`;
    }
    if (toolName === 'list_materials') {
      return `${output.count != null ? output.count : (Array.isArray(output.materials) ? output.materials.length : '?')} materiálů`;
    }
    if (toolName === 'get_offers') {
      return `${output.count != null ? output.count : (Array.isArray(output.offers) ? output.offers.length : 0)} nabídek`;
    }
    if (toolName === 'search_orders') {
      return `${output.count != null ? output.count : (Array.isArray(output.orders) ? output.orders.length : 0)} zakázek`;
    }
    if (toolName === 'find_customer') {
      const n = output.count != null ? output.count : (Array.isArray(output.customers) ? output.customers.length : 0);
      return `${n} ${n === 1 ? 'zákazník' : (n >= 2 && n <= 4 ? 'zákazníci' : 'zákazníků')}`;
    }
    if (toolName === 'get_settings') {
      return 'Nastavení načteno';
    }
    if (toolName === 'search_products') {
      const n = output.count != null ? output.count : (Array.isArray(output.products) ? output.products.length : 0);
      return `${n} produktů v katalogu`;
    }
    if (toolName === 'get_product') {
      return output.product?.name ? `Produkt: ${output.product.name}` : 'Detail produktu';
    }
    // Fallback podle tvaru výstupu
    if (output.cenaCelkem != null) return `Spočítáno: ${output.cenaCelkem} Kč`;
    if (output.id) return String(output.id);
    if (output.count != null) return `${output.count} výsledků`;
    return JSON.stringify(output).slice(0, 60);
  }
  return String(output).slice(0, 60);
}

function appendUserBubble(text) {
  const div = document.createElement('div');
  div.className = 'self-end max-w-[85%] bg-blue-600 text-white rounded-2xl rounded-br-sm px-3.5 py-2.5 text-sm font-medium whitespace-pre-wrap';
  div.textContent = text;
  el.messages.appendChild(div);
}

function activeChat() {
  return state.chats.find((c) => c.id === state.activeChatId) || null;
}

function nowIso() { return new Date().toISOString(); }

function genId() {
  return `chat-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/** Lidsky čitelný "stáří" chatu pro sidebar. */
function relativeAge(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'právě teď';
  if (min < 60) return `před ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `před ${h} h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `před ${d} d`;
  const w = Math.floor(d / 7);
  if (w < 5) return `před ${w} týd.`;
  const mo = Math.floor(d / 30);
  return `před ${mo} měs.`;
}

function saveChats() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      chats: state.chats,
      activeChatId: state.activeChatId,
    }));
  } catch {}
}

/** Migrace starého jednochatového uložiště (tria-ai-chat) na nový formát. */
function migrateLegacyChat() {
  try {
    const raw = localStorage.getItem(LEGACY_KEY);
    if (!raw) return;
    const data = JSON.parse(raw);
    if (!Array.isArray(data.messages) || data.messages.length === 0) return;
    // Stejná očista osiřelých/duplicitních uživatelských zpráv jako dřív.
    const cleaned = [];
    for (const m of data.messages) {
      if (
        m.role === 'user' &&
        cleaned.length > 0 &&
        cleaned[cleaned.length - 1].role === 'user' &&
        cleaned[cleaned.length - 1].content === m.content
      ) continue;
      cleaned.push(m);
    }
    while (cleaned.length > 0 && cleaned[cleaned.length - 1].role === 'user') cleaned.pop();
    if (cleaned.length === 0) return;
    const firstUser = cleaned.find((m) => m.role === 'user');
    const title = deriveLocalTitle(firstUser ? firstUser.content : 'Nový chat');
    const chat = {
      id: genId(),
      title,
      createdAt: nowIso(),
      updatedAt: nowIso(),
      modelId: data.modelId || null,
      messages: cleaned,
    };
    state.chats.unshift(chat);
    state.activeChatId = chat.id;
    saveChats();
    localStorage.removeItem(LEGACY_KEY);
  } catch {}
}

/** Lokální fallback titulek (před AI generováním / když gateway nejede). */
function deriveLocalTitle(text) {
  const t = String(text || '').replace(/\s+/g, ' ').trim();
  if (!t) return 'Nový chat';
  return t.length > 48 ? t.slice(0, 48) + '…' : t;
}

function loadChats() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const data = JSON.parse(raw);
      if (Array.isArray(data.chats)) state.chats = data.chats;
    }
  } catch {}
  migrateLegacyChat();
  // Asistent se vždy otevírá na NOVÉM chatu (chat vznikne až první zprávou).
  // Dlouhé chaty mají zaplněný kontext a horší přesnost odpovědí – návrat ke
  // starší konverzaci je vědomá volba přes sidebar, ne default.
  state.activeChatId = null;
}

function newChat() {
  // Nevytvářet prázdný chat hned – vytvoří se až při prvním odeslání, aby
  // sidebar nebyl plný "Nový chat" položek. Jen resetujme aktivní stav.
  state.activeChatId = null;
  if (el.messages) el.messages.innerHTML = '';
  renderEmptyHint();
  renderHistorySidebar();
  saveChats();
  if (el.input) el.input.focus();
}

/** Vytvoří (a otevře) nový chat s první uživatelskou zprávou. Vrací chat. */
function createChatForFirstMessage(text) {
  const chat = {
    id: genId(),
    title: deriveLocalTitle(text),
    createdAt: nowIso(),
    updatedAt: nowIso(),
    modelId: state.modelId,
    messages: [{ role: 'user', content: text }],
  };
  state.chats.unshift(chat);
  state.activeChatId = chat.id;
  // Omez počet uložených chatů (nejstarší přes limit se zahodí).
  if (state.chats.length > MAX_CHATS) state.chats = state.chats.slice(0, MAX_CHATS);
  return chat;
}

function openChat(id) {
  if (state.streaming) state.abortController?.abort();
  const chat = state.chats.find((c) => c.id === id);
  if (!chat) return;
  state.activeChatId = id;
  if (chat.modelId && state.availableModels.includes(chat.modelId)) {
    state.modelId = chat.modelId;
    if (el.modelSelect) el.modelSelect.value = state.modelId;
  }
  renderHistory();
  renderHistorySidebar();
  saveChats();
  closeHistoryDrawer();
}

function deleteChat(id, ev) {
  ev?.stopPropagation();
  const idx = state.chats.findIndex((c) => c.id === id);
  if (idx === -1) return;
  state.chats.splice(idx, 1);
  if (state.activeChatId === id) {
    state.activeChatId = state.chats[0] ? state.chats[0].id : null;
    renderHistory();
  }
  renderHistorySidebar();
  saveChats();
}

/** Filtrování+řazení chatů pro sidebar (nejnovější nahoře, dle vyhledávání). */
function visibleChats() {
  const q = state.historySearch.trim().toLowerCase();
  let list = state.chats.slice();
  if (q) {
    list = list.filter((c) => {
      const hay = (c.title + ' ' + (c.messages || []).map((m) => m.content || '').join(' ')).toLowerCase();
      return hay.includes(q);
    });
  }
  list.sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0));
  return list;
}

function renderHistorySidebar() {
  if (!el.historyList) return;
  const list = visibleChats();
  if (list.length === 0) {
    el.historyList.innerHTML = `<div class="text-center text-ink-muted text-xs py-8">${state.historySearch ? 'Nic nenalezeno.' : 'Zatím žádné chaty. Zeptej se asistenta a chat se uloží.'}</div>`;
    return;
  }
  el.historyList.innerHTML = list.map((c) => {
    const active = c.id === state.activeChatId;
    const age = relativeAge(c.updatedAt || c.createdAt);
    return `
      <div data-chat-id="${escapeHtml(c.id)}" class="group chat-history-item cursor-pointer rounded-md px-2.5 py-2 border ${active ? 'border-blue-300 bg-blue-50' : 'border-transparent hover:bg-gray-50'}">
        <div class="flex items-start justify-between gap-2">
          <div class="min-w-0 flex-1">
            <div class="text-sm font-medium text-ink truncate">${escapeHtml(c.title)}</div>
            <div class="text-[11px] text-ink-muted mt-0.5">${escapeHtml(age)} · ${c.messages.length} msg</div>
          </div>
          <button data-delete-id="${escapeHtml(c.id)}" title="Smazat chat" class="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-600 transition-opacity shrink-0">
            <svg class="w-4 h-4" data-icon="trash"></svg>
          </button>
        </div>
      </div>`;
  }).join('');
  hydrateIcons(el.historyList);
}

function renderEmptyHint() {
  if (!el.messages) return;
  const c = activeChat();
  if ((!c || c.messages.length === 0) && !state.streaming) {
    const hint = document.createElement('div');
    hint.className = 'text-center text-ink-muted text-sm py-10';
    hint.innerHTML = `
      <div class="flex justify-center mb-3 text-blue-500"><svg class="w-8 h-8" data-icon="sparkles"></svg></div>
      <p>Ahoj! Zeptej se na cenu tisku nebo napiš, co potřebuješ spočítat.</p>
      <p class="mt-1 text-xs">Např.: <em>Spočítej 1000 ks vizitek 90×54 mm 4/4 na matnou krídu 135 g</em></p>
    `;
    hydrateIcons(hint);
    el.messages.appendChild(hint);
  }
}

function renderHistory() {
  if (!el.messages) return;
  el.messages.innerHTML = '';
  const c = activeChat();
  if (c) {
    for (const m of c.messages) {
      if (m.role === 'user') appendUserBubble(m.content);
      else {
        const b = createAssistantBubble();
        b.setText(m.content);
      }
    }
  }
  if (!c || c.messages.length === 0) renderEmptyHint();
  scrollToBottom();
}

function clearMessages() {
  // Zpětná kompatibilita pro starý button hook – v novém UI znamená „nový chat".
  newChat();
}

function saveHistory() {
  // Zpětná kompatibilita – nový kód používá saveChats() + update aktivního chatu.
  saveChats();
}

// Načtení dostupných modelů z /api/ai/models. S retry – při výpadku gateway/
// sítě se to zkusí znovu (3× po 5s), ať model-selector nezůstane prázdný kvůli
// jednorázovému selhání (inspirace ai-sdk-gateway-demo/use-available-models).
async function loadModels() {
  const MAX_RETRIES = 3;
  const RETRY_DELAY_MS = 5000;
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      const res = await fetch(`${API_BASE_URL}/ai/models`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (!data.success) throw new Error('neúspěšná odpověď');
      const d = data.data;
      state.availableModels = d.availableModels || [];
      state.defaultModel = d.defaultModel || state.defaultModel;
      if (!state.modelId || !state.availableModels.includes(state.modelId)) {
        state.modelId = state.defaultModel;
      }
      populateModelSelect();
      return;
    } catch (e) {
      if (attempt < MAX_RETRIES) {
        console.warn(`[ai] loadModels selhalo (pokus ${attempt + 1}/${MAX_RETRIES}), retry za ${RETRY_DELAY_MS}ms:`, e.message);
        await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
      } else {
        console.warn('[ai] loadModels failed po retry', e);
        // Fallback: alespoň default model, ať selector není prázdný.
        if (!state.availableModels.length) {
          state.availableModels = [state.defaultModel];
          if (!state.modelId) state.modelId = state.defaultModel;
          populateModelSelect();
        }
      }
    }
  }
}

function populateModelSelect() {
  if (!el.modelSelect) return;
  const models = state.availableModels.length ? state.availableModels : [state.defaultModel];
  const short = (m) => {
    // "openai/gpt-5-nano" → "gpt-5-nano"; ponech plný název, pokud není ve formátu provider/name
    const parts = String(m).split('/');
    return parts.length > 1 ? parts.slice(1).join('/') : m;
  };
  el.modelSelect.innerHTML = models.map((m) => `<option value="${escapeHtml(m)}">${escapeHtml(short(m))}</option>`).join('');
  el.modelSelect.value = state.modelId || state.defaultModel;
}

async function sendMessage(text) {
  if (!text.trim() || state.streaming) return;
  if (el.messages) {
    const hint = el.messages.querySelector('.text-center');
    if (hint) hint.remove();
  }
  showError('');

  // Vytvoř nový chat, pokud žádný není aktivní.
  let chat = activeChat();
  const wasNewChat = !chat;
  if (!chat) {
    chat = createChatForFirstMessage(text);
    renderHistorySidebar();
  } else {
    chat.messages.push({ role: 'user', content: text });
  }
  const chatIdForTitle = chat.id;
  const msgCountBeforeReply = chat.messages.length;

  // Kontext pro API: máme-li z dřívější komprese shrnutí, pošli
  // [shrnutí, zprávy od summaryUpto] místo celé historie – requesty tak
  // nerostou donekonečna a server nemusí shrnovat při každé zprávě.
  // UI vždy zobrazuje plnou historii z chat.messages.
  const sentUpto = (chat.summary && Number.isInteger(chat.summaryUpto) && chat.summaryUpto > 0)
    ? Math.min(chat.summaryUpto, chat.messages.length - 1)
    : 0;
  const contextMessages = sentUpto > 0
    ? [
        { role: 'system', content: `Shrnutí starší části konverzace (historie komprimována pro pokračování):\n\n${chat.summary}` },
        ...chat.messages.slice(sentUpto),
      ]
    : chat.messages;

  appendUserBubble(text);

  const bubble = createAssistantBubble();
  let assistantText = '';
  const toolNames = new Map();
  const toolInputs = new Map();
  const calcButtonsRendered = new Set();
  bubble.setText('');
  scrollToBottom();

  state.streaming = true;
  state.abortController = new AbortController();
  // Pojistka proti stuck streamu (model se zasekne na generování velkého
  // tool inputu apod.) – po 120s abort a umožni uživateli retry.
  const timeoutMs = 120000;
  const timeoutId = setTimeout(() => {
    if (state.streaming && state.abortController) {
      showError('AI asistent neodpovídá (timeout). Zkus to znovu.');
      state.abortController.abort();
    }
  }, timeoutMs);
  toggleStreamingUI(true);

  try {
    const res = await fetch(`${API_BASE_URL}/ai/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ messages: contextMessages, modelId: state.modelId }),
      signal: state.abortController.signal,
    });

    if (!res.ok) {
      let msg = `HTTP ${res.status}`;
      try { const j = await res.json(); msg = j.error || msg; } catch {}
      throw new Error(msg);
    }

    await readSSEStream(res.body, (part) => {
      switch (part.type) {
        case 'data-compression': {
          // Server komprimoval kontext → ulož shrnutí a posuň hranici, od které
          // se historie posílá doslovně. coveredCount se vztahuje k poslanému
          // poli (jehož první zprávou mohlo být předchozí shrnutí).
          const info = part.data || {};
          if (typeof info.summary === 'string' && info.summary && Number.isInteger(info.coveredCount) && info.coveredCount > 0) {
            const coveredReal = info.coveredCount - (sentUpto > 0 ? 1 : 0);
            chat.summary = info.summary;
            chat.summaryUpto = Math.min(sentUpto + Math.max(coveredReal, 0), Math.max(chat.messages.length - 1, 0));
          }
          break;
        }
        case 'text-start': {
          // Model píše text, zavolá tool a pokračuje dalším textovým blokem –
          // bez oddělovače by se věty slily („…bíláZde je kalkulace…").
          if (assistantText && !/\n\s*$/.test(assistantText)) {
            assistantText += '\n\n';
            bubble.setText(assistantText);
          }
          break;
        }
        case 'text-delta': {
          assistantText += part.delta || part.text || '';
          bubble.setText(assistantText);
          scrollToBottom();
          break;
        }
        case 'reasoning-delta': {
          bubble.appendReasoning(part.delta || part.text || '');
          scrollToBottom();
          break;
        }
        case 'reasoning': {
          // Některé verze streamu pošlou reasoning najednou (bez -delta).
          bubble.appendReasoning(part.text || part.reasoning || '');
          scrollToBottom();
          break;
        }
        case 'tool-input-start': {
          toolNames.set(part.toolCallId, part.toolName);
          bubble.addTool(part.toolCallId, part.toolName);
          break;
        }
        case 'tool-input-available': {
          if (part.toolName === 'calculate_price' && part.input) {
            toolInputs.set(part.toolCallId, part.input);
          }
          break;
        }
        case 'tool-output-available': {
          bubble.finishTool(part.toolCallId, part.output);
          const toolName = toolNames.get(part.toolCallId);
          const offers = extractOffers(toolName, part.output);
          if (offers.length) {
            offers.forEach((o) => bubble.addOfferCard(o));
            scrollToBottom();
          }
          // K výsledku kalkulace přidat tlačítko pro otevření ve formuláři.
          // Opakovaná volání se stejnými parametry (retry modelu) → jen jedno tlačítko.
          if (toolName === 'calculate_price' && part.output && !part.output.error) {
            const input = toolInputs.get(part.toolCallId);
            const key = input ? JSON.stringify(input) : null;
            if (input && !calcButtonsRendered.has(key)) {
              calcButtonsRendered.add(key);
              bubble.addCalcButton(input);
              scrollToBottom();
            }
          }
          break;
        }
        case 'error': {
          showError(part.errorText || 'Chyba při generování odpovědi.');
          break;
        }
      }
    });

    if (assistantText.trim()) {
      chat.messages.push({ role: 'assistant', content: assistantText });
    } else {
      // Žádný text ani tool – skryj zbylý thinking indikátor, ať nezůstane prázdná bublina.
      bubble.clearThinking();
    }
    chat.updatedAt = nowIso();
    chat.modelId = state.modelId;
    saveChats();
    renderHistorySidebar();

    // Po první odpovědi v novém chatu vygeneruj AI titulek (nahradí lokální fallback).
    if (wasNewChat && assistantText.trim()) {
      generateTitleForChat(chatIdForTitle).catch(() => {/* fallback zůstane */});
    }
  } catch (e) {
    if (e.name === 'AbortError') {
      // Ulož částečnou odpověď, pokud něco je
      if (assistantText.trim()) {
        chat.messages.push({ role: 'assistant', content: assistantText });
      }
      chat.updatedAt = nowIso();
      saveChats();
      renderHistorySidebar();
    } else {
      showError(e.message || 'Spojení s AI se nezdařilo.');
      console.error('[ai] sendMessage error', e);
      // Při chybě v novém chatu bez odpovědi zruš prázdný chat, ať nezůstane osiřelý.
      if (wasNewChat && msgCountBeforeReply === chat.messages.length) {
        const idx = state.chats.findIndex((c) => c.id === chat.id);
        if (idx !== -1) {
          state.chats.splice(idx, 1);
          state.activeChatId = null;
          saveChats();
          renderHistorySidebar();
        }
      }
    }
  } finally {
    clearTimeout(timeoutId);
    state.streaming = false;
    state.abortController = null;
    toggleStreamingUI(false);
    const c = activeChat();
    if (!c || c.messages.length === 0) renderEmptyHint();
  }
}

/** Vygeneruje AI titulek pro chat a aktualizuje sidebar. Lokální fallback zůstane, pokud selže. */
async function generateTitleForChat(chatId) {
  const chat = state.chats.find((c) => c.id === chatId);
  if (!chat) return;
  const res = await fetch(`${API_BASE_URL}/ai/title`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ messages: chat.messages, modelId: chat.modelId || state.modelId }),
  });
  if (!res.ok) return;
  const r = await res.json();
  if (!r || !r.success || !r.data || !r.data.title) return;
  // Jen pokud je chat stále aktuální (uživatel ho mezitím nesmazal).
  const target = state.chats.find((c) => c.id === chatId);
  if (!target) return;
  target.title = String(r.data.title).slice(0, 80);
  saveChats();
  renderHistorySidebar();
}

/** Parsuje SSE UI message stream (data: <json>\n\n, končí data: [DONE]). */
async function readSSEStream(body, onPart) {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx;
    while ((idx = buffer.indexOf('\n\n')) !== -1) {
      const block = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      const lines = block.split('\n');
      const dataLines = lines.filter((l) => l.startsWith('data:')).map((l) => l.slice(5).trimStart());
      if (!dataLines.length) continue;
      const payload = dataLines.join('\n');
      if (payload === '[DONE]') return;
      try { onPart(JSON.parse(payload)); } catch { /* ignore malformed */ }
    }
  }
}

function toggleStreamingUI(on) {
  el.sendBtn?.classList.toggle('hidden', on);
  el.stopBtn?.classList.toggle('hidden', !on);
  if (el.input) {
    if (on) el.input.setAttribute('disabled', 'true');
    else el.input.removeAttribute('disabled');
  }
}

export function initAiAssistant() {
  if (state.initialised) {
    // Již inicializováno – jen dočti modely (např. po změně v Nastavení).
    loadModels();
    return;
  }
  el = {
    messages: $('ai-messages'),
    input: $('ai-input'),
    form: $('ai-chat-form'),
    sendBtn: $('ai-send-btn'),
    stopBtn: $('ai-stop-btn'),
    newChatBtn: $('ai-new-chat-btn'),
    modelSelect: $('ai-model-select'),
    error: $('ai-error'),
    historyList: $('ai-history-list'),
    historySearch: $('ai-history-search'),
    historyToggleBtn: $('ai-history-toggle-btn'),
    historySidebar: $('ai-history-sidebar'),
    historyOverlay: $('ai-history-overlay'),
    attachBtn: $('ai-attach-btn'),
    attachInput: $('ai-attach-input'),
    attachChips: $('ai-attach-chips'),
    attachNote: $('ai-attach-note'),
  };

  if (!el.messages || !el.form) return; // sekce není v DOM (veřejné stránky)

  state.initialised = true;

  loadChats();
  loadModels();

  el.form.addEventListener('submit', (e) => {
    e.preventDefault();
    const text = el.input.value.trim();
    if (!text) return;
    el.input.value = '';
    sendMessage(text);
  });

  el.input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      el.form.requestSubmit();
    }
  });

  el.stopBtn?.addEventListener('click', () => state.abortController?.abort());
  el.newChatBtn?.addEventListener('click', () => {
    if (state.streaming) state.abortController?.abort();
    newChat();
  });

  el.modelSelect?.addEventListener('change', () => {
    state.modelId = el.modelSelect.value;
    const c = activeChat();
    if (c) { c.modelId = state.modelId; saveChats(); }
  });

  // Historie – klik otevře chat, mazací tlačítko smaže.
  el.historyList?.addEventListener('click', (e) => {
    const delBtn = e.target.closest('[data-delete-id]');
    if (delBtn) {
      e.preventDefault();
      deleteChat(delBtn.getAttribute('data-delete-id'), e);
      return;
    }
    const item = e.target.closest('[data-chat-id]');
    if (item) openChat(item.getAttribute('data-chat-id'));
  });

  el.historySearch?.addEventListener('input', (e) => {
    state.historySearch = e.target.value || '';
    renderHistorySidebar();
  });

  // Mobilní drawer s historií.
  el.historyToggleBtn?.addEventListener('click', openHistoryDrawer);
  el.historyOverlay?.addEventListener('click', closeHistoryDrawer);

  // Přílohy – zatím jen vizuální nálepky (soubor se neodešle na server).
  el.attachBtn?.addEventListener('click', () => el.attachInput?.click());
  el.attachInput?.addEventListener('change', (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    renderAttachChips(files);
    el.attachNote?.classList.remove('hidden');
    e.target.value = '';
  });

  renderHistory();
  renderHistorySidebar();
}

function renderAttachChips(files) {
  if (!el.attachChips) return;
  for (const f of files) {
    const chip = document.createElement('span');
    chip.className = 'inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 border border-gray-200';
    chip.innerHTML = `
      <svg class="w-3 h-3" data-icon="paperclip"></svg>
      <span class="max-w-[160px] truncate">${escapeHtml(f.name)}</span>
      <button type="button" class="text-gray-400 hover:text-red-600" title="Odebrat">
        <svg class="w-3 h-3" data-icon="x-mark"></svg>
      </button>
    `;
    chip.querySelector('button').addEventListener('click', () => {
      chip.remove();
      if (el.attachChips && el.attachChips.children.length === 0) {
        el.attachNote?.classList.add('hidden');
      }
    });
    hydrateIcons(chip);
    el.attachChips.appendChild(chip);
  }
}

function openHistoryDrawer() {
  if (!el.historySidebar) return;
  el.historySidebar.classList.remove('hidden', 'lg:flex');
  el.historySidebar.classList.add('fixed', 'inset-y-0', 'left-0', 'z-50', 'flex', 'w-80', 'shadow-xl');
  el.historyOverlay?.classList.remove('hidden');
}

function closeHistoryDrawer() {
  if (!el.historySidebar) return;
  // Na lg zůstává viditelný jako flex sloupec, na menších se skryje.
  el.historySidebar.classList.remove('fixed', 'inset-y-0', 'left-0', 'z-50', 'flex', 'w-80', 'shadow-xl');
  el.historySidebar.classList.add('hidden', 'lg:flex');
  el.historyOverlay?.classList.add('hidden');
}

// ──────────────────────────────────────────────
// Nastavení → AI panel (načtení/uložení konfigurace)
// ──────────────────────────────────────────────
export function initAiSettingsPanel() {
  const panel = $('settings-ai');
  if (!panel) return;

  const apiKeyInput = $('ai-gatewayApiKey');
  const baseUrlInput = $('ai-baseUrl');
  const enabledInput = $('ai-enabled');
  const defaultSelect = $('ai-defaultModel');
  const reasoningSelect = $('ai-reasoning');
  const modelsList = $('ai-models-list');
  const loadBtn = $('ai-load-models-btn');
  const saveBtn = $('ai-save-btn');
  const statusEl = $('ai-models-status');
  const saveStatus = $('ai-save-status');

  let gatewayModels = [];
  let selectedModels = [];
  // Snapshot celého ai bloku – PUT /settings merguje jen top-level klíče,
  // takže při uložení musíme poslat i klíče, které panel needituje
  // (contextWindows, providerOptions…), jinak by se ztratily.
  let currentAi = {};

  const apiBase = API_BASE_URL;

  async function loadSettings() {
    try {
      const res = await fetch(`${apiBase}/settings`);
      if (!res.ok) return;
      const r = await res.json();
      const ai = (r.data && r.data.settings && r.data.settings.ai) || {};
      currentAi = ai;
      apiKeyInput.value = ai.gatewayApiKey || '';
      baseUrlInput.value = ai.baseUrl || '';
      enabledInput.checked = ai.enabled !== false;
      if (reasoningSelect) reasoningSelect.value = ai.reasoning || 'default';
      selectedModels = Array.isArray(ai.availableModels) ? [...ai.availableModels] : [];
      renderModelsList();
      setDefaultSelect(ai.defaultModel || selectedModels[0] || 'deepseek/deepseek-v4-flash');
    } catch (e) { console.warn('[ai-settings] load', e); }
  }

  function setDefaultSelect(value) {
    const opts = (selectedModels.length ? selectedModels : gatewayModels.map(m => m.id)).concat(
      value && !(selectedModels.includes(value) || gatewayModels.some(m => m.id === value)) ? [value] : []
    );
    defaultSelect.innerHTML = opts.map((m) => `<option value="${escapeHtml(m)}">${escapeHtml(m)}</option>`).join('');
    defaultSelect.value = value;
  }

  function renderModelsList() {
    const pool = gatewayModels.length ? gatewayModels : selectedModels.map((id) => ({ id, name: id }));
    modelsList.innerHTML = pool.map((m) => {
      const checked = selectedModels.includes(m.id) ? 'checked' : '';
      return `
        <label class="flex items-center gap-2 text-sm px-2.5 py-1.5 border border-gray-200 rounded-md hover:bg-gray-50">
          <input type="checkbox" value="${escapeHtml(m.id)}" ${checked} class="ai-model-checkbox rounded border-gray-300" />
          <span class="truncate">${escapeHtml(m.name || m.id)}</span>
        </label>`;
    }).join('');
    modelsList.querySelectorAll('.ai-model-checkbox').forEach((cb) => {
      cb.addEventListener('change', () => {
        selectedModels = Array.from(modelsList.querySelectorAll('.ai-model-checkbox:checked')).map((c) => c.value);
        setDefaultSelect(defaultSelect.value && selectedModels.includes(defaultSelect.value) ? defaultSelect.value : selectedModels[0]);
      });
    });
  }

  loadBtn?.addEventListener('click', async () => {
    statusEl.textContent = 'Načítám modely z gateway…';
    try {
      const res = await fetch(`${apiBase}/ai/models?source=gateway`);
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || `HTTP ${res.status}`);
      }
      const r = await res.json();
      gatewayModels = (r.data && r.data.models) || [];
      if (r.data && Array.isArray(r.data.availableModels)) selectedModels = [...r.data.availableModels];
      statusEl.textContent = `Nalezeno ${gatewayModels.length} modelů.`;
      renderModelsList();
      setDefaultSelect((r.data && r.data.defaultModel) || selectedModels[0] || 'deepseek/deepseek-v4-flash');
    } catch (e) {
      statusEl.textContent = `Chyba: ${e.message}`;
    }
  });

  saveBtn?.addEventListener('click', async () => {
    saveStatus.textContent = 'Ukládám…';
    const payload = {
      ai: {
        ...currentAi,
        enabled: enabledInput.checked,
        gatewayApiKey: apiKeyInput.value.trim(),
        baseUrl: baseUrlInput.value.trim(),
        defaultModel: defaultSelect.value,
        reasoning: reasoningSelect ? reasoningSelect.value : (currentAi.reasoning || 'default'),
        availableModels: selectedModels.length ? selectedModels : [defaultSelect.value],
      },
    };
    try {
      const res = await fetch(`${apiBase}/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      currentAi = payload.ai;
      saveStatus.textContent = 'Uloženo ✓';
      setTimeout(() => { saveStatus.textContent = ''; }, 2500);
      // Znovu načti modely do chat selectoru.
      state.initialised && loadModels();
    } catch (e) {
      saveStatus.textContent = `Chyba: ${e.message}`;
    }
  });

  // Cron: načti nastavení při prvním zobrazení panelu (lazy, až je sekce viditelná).
  let loaded = false;
  const tryLoad = () => { if (!loaded) { loaded = true; loadSettings(); } };
  if (document.getElementById('nastaveni-section')?.classList.contains('active')) tryLoad();
  document.addEventListener('click', (e) => {
    const link = e.target.closest('[data-section="nastaveni"]');
    if (link) setTimeout(tryLoad, 200);
  }, { once: false });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAiSettingsPanel);
} else {
  initAiSettingsPanel();
}
